import { Vector2 } from 'three';
import type { CameraFraming, FrameSize, RenderTarget } from '../../visual/index.js';
import type { RenderingPrototype } from '../prototype/RenderingPrototype.js';

/**
 * The drawing half of an offline render.
 *
 * Everything the session knows about three lives here. It resizes the renderer,
 * points the camera for an aspect ratio, draws a frame and hands back PNG bytes
 * — four operations, none of which the session could describe for itself.
 *
 * The camera work is the part worth reading. Resizing a canvas to 9:16 and
 * drawing the same scene crops it: the composition is radial and the horizontal
 * field of view shrinks with the width, so the mandala's edges leave the frame.
 * A vertical export has to *pull back*, and by exactly the amount the narrower
 * edge lost, which is what `frame` computes.
 */
export class OfflineRenderTarget implements RenderTarget {
  private restoreSize: { width: number; height: number } | null = null;
  private restorePixelRatio: number | null = null;
  private restoreFov: number | null = null;
  private restoreAspect: number | null = null;

  constructor(private readonly prototype: RenderingPrototype) {}

  /**
   * The largest render target this GPU will accept.
   *
   * Read from the driver rather than assumed. A 4K request on a machine capped
   * at 2048 produces an incomplete framebuffer and a black frame — a failure
   * that looks like a bug in the artwork, so it is worth refusing up front.
   */
  get maxDimension(): number {
    const gl = this.prototype.stage.renderer.getContext();
    const max = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    return typeof max === 'number' && max > 0 ? max : 4096;
  }

  resize(size: FrameSize): void {
    const renderer = this.prototype.stage.renderer;
    if (this.restoreSize === null) {
      const current = renderer.getSize(new Vector2());
      this.restoreSize = { width: current.x, height: current.y };
      this.restorePixelRatio = renderer.getPixelRatio();
    }
    // Pixel ratio 1: the requested size *is* the output size. Leaving the
    // display ratio in would silently render a 4K export at 8K on a retina
    // machine and at 4K elsewhere, which is the opposite of reproducible.
    renderer.setPixelRatio(1);
    renderer.setSize(size.width, size.height, false);
    this.prototype.stage.resizeTargets(size.width, size.height);
  }

  frame(framing: CameraFraming): void {
    const camera = this.prototype.stage.camera;
    if (this.restoreFov === null) {
      this.restoreFov = camera.fov;
      this.restoreAspect = camera.aspect;
    }
    camera.aspect = framing.aspect;

    // The field is a disc, so whichever edge is shorter decides how much of it
    // fits. For a wide frame that is the height and the vertical FOV already
    // describes it; for a tall one it is the width, and the vertical FOV has to
    // grow so the horizontal extent still contains the disc.
    const baseFov = this.restoreFov;
    if (framing.aspect >= 1) {
      camera.fov = baseFov;
    } else {
      const halfBase = (baseFov * Math.PI) / 360;
      // Vertical half-angle that yields the same horizontal half-angle a 16:9
      // frame had, so the disc's width is preserved rather than its height.
      const targetHorizontal = Math.atan(Math.tan(halfBase) * (16 / 9));
      const needed = Math.atan(Math.tan(targetHorizontal) / framing.aspect);
      camera.fov = Math.min(140, (needed * 360) / Math.PI);
    }
    camera.fov /= Math.max(0.25, framing.scale);
    camera.updateProjectionMatrix();
  }

  /**
   * Draws one frame, with the world posed at the render's own time.
   *
   * `renderAtTime` rather than `renderFrame`: the latter reads `ViewClock`,
   * which is wall time, and the starfield and mandala would then depend on when
   * the export was started rather than on which frame it is. `stage.render()`
   * alone is not enough either — it draws the scene without posing it, so the
   * background freezes at whatever it happened to be showing.
   */
  render(time: number, deltaTime: number): void {
    this.prototype.renderAtTime(time, deltaTime);
  }

  async capture(): Promise<Uint8Array | null> {
    const canvas = this.prototype.stage.renderer.domElement;
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((result) => resolve(result), 'image/png');
    });
    if (blob === null) return null;
    return new Uint8Array(await blob.arrayBuffer());
  }

  /**
   * A fingerprint of the drawn frame.
   *
   * Sampled on a coarse grid rather than hashed pixel by pixel: a 4K frame is
   * eight million pixels and this runs on a handful of frames purely to answer
   * "did two renders draw the same thing". A 64×64 sample of a composition this
   * structured will differ long before a human would call two frames the same.
   */
  async hash(): Promise<string> {
    const canvas = this.prototype.stage.renderer.domElement;
    const gl = this.prototype.stage.renderer.getContext();
    const width = canvas.width;
    const height = canvas.height;
    const stepX = Math.max(1, Math.floor(width / 64));
    const stepY = Math.max(1, Math.floor(height / 64));

    // One row at a time: a full-frame readback of a 4K target is 33 MB, and the
    // grid only needs 64 of those rows.
    const row = new Uint8Array(width * 4);
    let hash = 0x811c9dc5 ^ width ^ (height << 16);
    for (let y = 0; y < height; y += stepY) {
      gl.readPixels(0, y, width, 1, gl.RGBA, gl.UNSIGNED_BYTE, row);
      for (let x = 0; x < width; x += stepX) {
        const offset = x * 4;
        // Green channel alone: enough to separate frames, a third of the work.
        hash ^= row[offset + 1] as number;
        hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) | 0;
      }
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
  }

  /** Puts the canvas and camera back the way the live game left them. */
  restore(): void {
    const renderer = this.prototype.stage.renderer;
    if (this.restoreSize !== null) {
      renderer.setPixelRatio(this.restorePixelRatio ?? 1);
      renderer.setSize(this.restoreSize.width, this.restoreSize.height, false);
      this.restoreSize = null;
      this.restorePixelRatio = null;
    }
    const camera = this.prototype.stage.camera;
    if (this.restoreFov !== null) {
      camera.fov = this.restoreFov;
      camera.aspect = this.restoreAspect ?? camera.aspect;
      camera.updateProjectionMatrix();
      this.restoreFov = null;
      this.restoreAspect = null;
    }
    this.prototype.resize();
  }
}
